import React, { useState } from 'react';
import useNCZICommandProcess from '../../../Hooks/useNCZICommandProcess';
import { useEffectAsync } from '../../../../../helpers';
import { GetPatientInsuranceSP } from '../../../Commands/GetPatientInsuranceSP';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import Loading from '../../../../../Components/Loading';
import Text from '../../../../../Components/Base/Text/Text';
import { NCZIExtension } from '../../../Interfaces/ncziTypes';

//@ts-ignore
import styles from './InsuranceExemptionList.module.scss';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import { joinClasses } from '../../../../../Utils/className.util';
import Button from '../../../../../Components/Button/Button';
import SubmitButton from '../../../../../Components/Button/SubmitButton';

interface Props {
	onSubmit: (values: string[]) => void;
	patientIdentifier: string;
	initialExemptionedItems?: string[];
}

export type InsuranceExemptionItem = {
	recordId: NCZIExtension;
	icr: number;
	role: string;
	validFrom: string;
	validTo: string | null;
	company: {
		vs: number;
		name: string;
		identificationNumber: number;
		vatNumber: number;
	};
};

const InsuranceExemptionList: React.FC<Props> = props => {
	const { onSubmit, patientIdentifier, initialExemptionedItems } = props;
	const [items, setItems] = useState<InsuranceExemptionItem[] | undefined>(
		undefined,
	);
	const [exemptionedItems, setExemptionedItems] = useState<string[]>([]);

	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: (commandId, result) => {
				console.log({ insuranceItems: result.insuranceItems });
				setItems(result.insuranceItems);
			},
			hideSuccess: true,
		});

	useEffectAsync(async () => {
		const GetCommand = GetPatientInsuranceSP({
			patientCode: patientIdentifier,
		});

		await processCommand(GetCommand);

		setExemptionedItems(initialExemptionedItems || []);
	}, []);

	const handleSet = (recordId: string) => {
		const tmpItems = [...exemptionedItems, recordId];
		setExemptionedItems(tmpItems);
	};

	const handleUnSet = (recordId: string) => {
		const tmpItems = [...exemptionedItems];
		const index = tmpItems.indexOf(recordId);
		if (index !== -1) {
			tmpItems.splice(index, 1);
		}
		setExemptionedItems(tmpItems);
	};

	const handleSubmit = () => {
		onSubmit(exemptionedItems);
	};

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	if (typeof items === 'undefined') {
		return <Loading></Loading>;
	}

	if (!items.length) {
		return (
			<div className={styles.wrapper}>
				<Text tx={'nczi.noItemsFound'} firstUpper />
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			{items.length === exemptionedItems.length && (
				<div className={styles.warning}>
					<Text
						tx={'nczi.electronicDPN.create.insuranceExemption.nonStandard'}
					/>
				</div>
			)}
			{items.map((item, index) => {
				const isExemptioned =
					exemptionedItems.indexOf(item.recordId.code) !== -1;
				return (
					<div
						className={joinClasses([
							styles.row,
							isExemptioned ? styles.exemptioned : '',
						])}
						key={index}>
						<div className={styles.name}>{item.company.name}</div>
						<div className={styles.identificationNumber}>
							{item.company.identificationNumber}
						</div>
						<div className={styles.role}>{item.role}</div>
						<div className={styles.controls}>
							{!isExemptioned && (
								<LinkButton
									firstUpper
									title={'nczi.electronicDPN.create.insuranceExemption.set'}
									theme={Theme.RED}
									onClick={() => handleSet(item.recordId.code)}
								/>
							)}
							{isExemptioned && (
								<LinkButton
									firstUpper
									title={'nczi.electronicDPN.create.insuranceExemption.unset'}
									theme={Theme.BLUE}
									onClick={() => handleUnSet(item.recordId.code)}
								/>
							)}
						</div>
					</div>
				);
			})}
			<Button
				title={'component.nczi.setup.save'}
				className={styles.submit}
				onClick={handleSubmit}
			/>
		</div>
	);
};

export default InsuranceExemptionList;
